/**
 * Which design of each slot a page draws.
 *
 * A membership of templateManifest.ts is a slot: one place on a page with one
 * or more designs (members), oldest first. A page draws one member per slot:
 *
 * - A page without a saved scene follows the live choice: the member keys that
 *   a superadmin chose (`chosen`, Mongo `template_slots`), else the slot's code
 *   default (`key`).
 * - A saved scene follows its own snapshot (scene field `templateMembers`): the
 *   member it was saved with, else the slot's scene fallback. This is normally
 *   its first member. A slot that restores an older design names the fallback
 *   explicitly, so existing scenes keep the design they already draw.
 *
 * The pools of the Templates tab follow from the same order: a member newer
 * than the one on air is a candidate, an older one is in the archive.
 */

import {materializeDefaultSceneVisibility} from "./defaultSceneVisibility";
import {
  KNOWN_TEMPLATE_IDS_BY_CHANNEL,
  TEMPLATE_SLOTS_BY_CHANNEL,
  getTemplateDefinitionByKey,
  releasesSetTemplateIds,
  templatesOfProduct,
  type TemplateMetadata,
  type TemplateSlot,
} from "./templateManifest";

export type TemplateSelection =
  | {source: "live"; chosen: readonly string[]}
  | {source: "scene"; members: readonly string[]};

/** Code alone: every slot draws its `key`. */
export const CODE_SELECTION: TemplateSelection = Object.freeze({
  source: "live",
  chosen: Object.freeze([]) as readonly string[],
});

export type TemplatePool = "on-air" | "candidate" | "archive";

/** The slot functions over one set of slots. Tests build their own from synthetic memberships. */
export function createTemplateSlotIndex(slotsByChannel: Record<string, TemplateSlot[]>) {
  function slotsOf(channel: string): TemplateSlot[] {
    const slots = slotsByChannel[channel];
    if (!slots) throw new Error(`Unknown channel "${channel}"`);
    return slots;
  }

  const allSlots: readonly TemplateSlot[] = Object.values(slotsByChannel).flat();

  /** The slots with two or more designs: the only ones a choice can change. */
  const multiMemberSlots: readonly TemplateSlot[] = allSlots.filter(
    (slot) => slot.members.length > 1
  );

  /** The templates a channel draws under a selection: one per slot, in channel order. */
  function drawnTemplates(channel: string, selection: TemplateSelection): TemplateMetadata[] {
    return slotsOf(channel).map((slot) => slotMember(slot, selection));
  }

  /** The templates a channel draws for a product under a selection: one per id. */
  function drawnTemplatesForProduct(
    channel: string,
    product: string,
    selection: TemplateSelection
  ): TemplateMetadata[] {
    return templatesOfProduct(drawnTemplates(channel, selection), product);
  }

  /** The ids each releases set draws under a selection, by set. */
  function drawnReleasesSetTemplateIds(selection: TemplateSelection): Record<string, string[]> {
    return releasesSetTemplateIds(drawnTemplates("releases", selection));
  }

  /** Every id of the slots that hold `id` in a channel: `[id]` for a slot of one design. */
  function slotTemplateIds(channel: string, id: string): string[] {
    const ids = slotsOf(channel)
      .filter((slot) => slot.members.some((member) => member.id === id))
      .flatMap((slot) => slot.members.map((member) => member.id));
    return ids.length > 0 ? [...new Set(ids)] : [id];
  }

  /**
   * The ids of a page's id list (`hiddenTemplates`) with every design of their
   * slots: an id names its place on the page, so a page that removed "Splash"
   * also removes the design that replaced it.
   */
  function withSlotTemplateIds(channel: string, ids: readonly string[] | null | undefined): string[] {
    if (!ids || ids.length === 0) return [];
    return [...new Set(ids.flatMap((id) => slotTemplateIds(channel, id)))];
  }

  /**
   * `ids` with the place of `id` in it or out of it: every design of its slot
   * leaves the list, and `id` joins it when `included`.
   */
  function withSlotTemplateId(
    channel: string,
    ids: readonly string[],
    id: string,
    included: boolean
  ): string[] {
    const slotIds = new Set(slotTemplateIds(channel, id));
    const rest = ids.filter((item) => !slotIds.has(item));
    return included ? [...rest, id] : rest;
  }

  /**
   * The id a channel draws in place of `id` under a selection: the member on
   * air of its slot. An id of no slot is returned as it is. A page's added
   * banners (`extraTemplates`) read this, so an added "Splash" follows a swap.
   */
  function drawnTemplateId(channel: string, id: string, selection: TemplateSelection): string {
    const slot = slotsOf(channel).find((candidate) =>
      candidate.members.some((member) => member.id === id)
    );
    return slot ? slotMember(slot, selection).id : id;
  }

  /**
   * The members a scene keeps: the drawn member of every slot of the channel
   * with two or more designs, in channel order. The scene draws them whatever
   * the live choice is later.
   */
  function templateMembersSnapshot(channel: string, selection: TemplateSelection): string[] {
    return slotsOf(channel)
      .filter((slot) => slot.members.length > 1)
      .map((slot) => slotMember(slot, selection).key);
  }

  /**
   * The selection of a saved scene, and the keys of it that this build does
   * not know. A caller must not draw a scene with unknown keys: a renderer or
   * a tab older than the scene would draw another design. A key this build
   * knows is fine even where its slot has one design here: an older renderer
   * draws that design anyway.
   */
  function parseSceneTemplateMembers(
    channel: string,
    members: unknown
  ): {selection: TemplateSelection; unknown: string[]} {
    if (members === undefined || members === null) {
      return {selection: {source: "scene", members: []}, unknown: []};
    }
    if (!Array.isArray(members) || members.some((key) => typeof key !== "string")) {
      throw new Error("scene.templateMembers must be an array of template keys");
    }
    const known = new Set(
      slotsOf(channel).flatMap((slot) => slot.members.map((member) => member.key))
    );
    return {
      selection: {source: "scene", members: members.filter((key) => known.has(key))},
      unknown: members.filter((key) => !known.has(key)),
    };
  }

  /** The selection of a saved scene; an unknown key is an error. */
  function sceneTemplateSelection(channel: string, members: unknown): TemplateSelection {
    const {selection, unknown} = parseSceneTemplateMembers(channel, members);
    if (unknown.length > 0) {
      throw new Error(
        `This scene uses template designs that this version does not have: ${unknown.join(", ")}`
      );
    }
    return selection;
  }

  /**
   * The live selection from the stored choice. A stored key that this build
   * does not know is left out, and its slot draws the code default.
   */
  function liveTemplateSelection(chosen: readonly string[]): {
    selection: TemplateSelection;
    unknown: string[];
  } {
    const known = new Set(multiMemberSlots.flatMap((slot) => slot.members.map((m) => m.key)));
    return {
      selection: {source: "live", chosen: chosen.filter((key) => known.has(key))},
      unknown: chosen.filter((key) => !known.has(key)),
    };
  }

  /**
   * The stored choice after a superadmin puts `member` on air in every slot
   * that lists it. Its siblings leave the choice; the member joins it unless
   * it is the code default of each of those slots.
   */
  function chooseTemplateMember(chosen: readonly string[], member: string): string[] {
    const slots = multiMemberSlots.filter((slot) => slot.members.some((m) => m.key === member));
    if (slots.length === 0) {
      throw new Error(`Template "${member}" is not a design of a slot with two or more designs`);
    }
    const siblings = new Set(slots.flatMap((slot) => slot.members.map((m) => m.key)));
    const rest = chosen.filter((key) => !siblings.has(key));
    const isDefaultEverywhere = slots.every((slot) => slot.defaultMember.key === member);
    return isDefaultEverywhere ? rest : [...rest, member];
  }

  /**
   * The pool of every template key under a live selection: on air when a slot
   * draws it; otherwise in the archive when it is older than the member on air
   * of one of its slots, and a candidate when it is newer in all of them.
   */
  function templatePools(selection: TemplateSelection): Map<string, TemplatePool> {
    const pools = new Map<string, TemplatePool>();
    for (const slot of allSlots) {
      const drawn = slotMember(slot, selection);
      const drawnIndex = slot.members.indexOf(drawn);
      slot.members.forEach((member, index) => {
        const pool: TemplatePool =
          member === drawn ? "on-air" : index < drawnIndex ? "archive" : "candidate";
        const previous = pools.get(member.key);
        if (previous && POOL_RANK[previous] >= POOL_RANK[pool]) return;
        pools.set(member.key, pool);
      });
    }
    return pools;
  }

  /** The slots that list a template key, in every channel. */
  function slotsOfTemplateKey(key: string): TemplateSlot[] {
    getTemplateDefinitionByKey(key);
    return allSlots.filter((slot) => slot.members.some((member) => member.key === key));
  }

  return {
    slotsOf,
    allSlots,
    multiMemberSlots,
    drawnTemplates,
    drawnTemplatesForProduct,
    drawnReleasesSetTemplateIds,
    slotTemplateIds,
    withSlotTemplateIds,
    withSlotTemplateId,
    drawnTemplateId,
    templateMembersSnapshot,
    parseSceneTemplateMembers,
    sceneTemplateSelection,
    liveTemplateSelection,
    chooseTemplateMember,
    templatePools,
    slotsOfTemplateKey,
  };
}

/** On air beats archive, archive beats candidate, for a key in several slots. */
const POOL_RANK: Record<TemplatePool, number> = {"on-air": 2, archive: 1, candidate: 0};

/** The member a slot draws under a selection. */
export function slotMember(slot: TemplateSlot, selection: TemplateSelection): TemplateMetadata {
  if (slot.members.length === 1) return slot.members[0];
  const picked = selection.source === "live" ? selection.chosen : selection.members;
  const match = slot.members.find((member) => picked.includes(member.key));
  if (match) return match;
  return selection.source === "live" ? slot.defaultMember : slot.sceneFallbackMember;
}

const index = createTemplateSlotIndex(TEMPLATE_SLOTS_BY_CHANNEL);

export const MULTI_MEMBER_SLOTS = index.multiMemberSlots;
export const ALL_TEMPLATE_SLOTS = index.allSlots;
export const {
  drawnTemplates,
  drawnTemplatesForProduct,
  drawnReleasesSetTemplateIds,
  slotTemplateIds,
  withSlotTemplateIds,
  withSlotTemplateId,
  drawnTemplateId,
  templateMembersSnapshot,
  parseSceneTemplateMembers,
  sceneTemplateSelection,
  liveTemplateSelection,
  chooseTemplateMember,
  templatePools,
  slotsOfTemplateKey,
} = index;

/**
 * A default row's eyes for every design of the channel, on air or not
 * (defaultSceneVisibility.ts). The server's resolver and the client's flag-off
 * cascade both read this, so they cannot disagree.
 */
export function materializeChannelDefaultVisibility<T extends Record<string, unknown> | null | undefined>(
  scene: T,
  channel: string | null | undefined
): T {
  const ids = channel ? KNOWN_TEMPLATE_IDS_BY_CHANNEL[channel] : undefined;
  if (!channel || !ids) return materializeDefaultSceneVisibility(scene, []);
  return materializeDefaultSceneVisibility(scene, ids, (id) => slotTemplateIds(channel, id));
}
